import React, { useState, useEffect } from 'react';
import { X, Phone, ChevronRight } from 'lucide-react';
import { useSettingsStore } from '../stores/settingsStore';

// ─── Config ───────────────────────────────────────────────────────────────────
const WORK_HOURS = { start: 8, end: 20 };

// ─── 2 Topics only ────────────────────────────────────────────────────────────
const TOPICS = [
  {
    id: 'order',
    emoji: '📦',
    label: 'Sipariş Destek',
    sublabel: 'Sipariş, kargo ve teslimat',
    message: 'Merhaba! Siparişim hakkında destek almak istiyorum.',
  },
  {
    id: 'technical',
    emoji: '🔧',
    label: 'Teknik Destek',
    sublabel: 'Arıza, bakım ve servis',
    message: 'Merhaba! Teknik destek almak istiyorum.',
  },
];

function isOnline(): boolean {
  const now = new Date();
  const day = now.getDay();
  const hour = now.getHours();
  if (day === 0) return false;
  return hour >= WORK_HOURS.start && hour < WORK_HOURS.end;
}

const WhatsAppSVG = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const WhatsAppButton: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const online = isOnline();
  const waPhone = useSettingsStore(s => s.whatsappNumber) || '905321234567';
  const phoneNumber = useSettingsStore(s => s.phoneNumber) || '';

  useEffect(() => {
    const timer = setTimeout(() => setShowHint(true), 5000);
    return () => clearTimeout(timer);
  }, []);

  const openWhatsApp = (message: string) => {
    const url = `https://wa.me/${waPhone}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    setIsOpen(false);
  };

  const handleOpen = () => {
    setIsOpen(true);
    setShowHint(false);
  };

  return (
    <div className="fixed bottom-6 left-6 z-40 flex flex-col items-start gap-2">

      {/* Popup */}
      {isOpen && (
        <div
          className="w-72 bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100 mb-2"
          style={{ animation: 'slideUpFadeIn 0.2s ease-out' }}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#075E54] to-[#128C7E] px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <WhatsAppSVG className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-white font-bold text-sm">WhatsApp Destek</p>
                <div className="flex items-center gap-1">
                  <span className={`w-1.5 h-1.5 rounded-full ${online ? 'bg-green-400' : 'bg-gray-400'}`} />
                  <p className="text-white/80 text-xs">{online ? 'Çevrimiçi' : 'Mesai dışı'}</p>
                </div>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="w-7 h-7 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
            >
              <X size={14} className="text-white" />
            </button>
          </div>

          {/* Topics */}
          <div className="p-3 space-y-2">
            <p className="text-xs text-gray-400 px-1">Konunuzu seçin:</p>
            {TOPICS.map(topic => (
              <button
                key={topic.id}
                onClick={() => openWhatsApp(topic.message)}
                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-green-50 border border-transparent hover:border-green-200 transition-all group text-left"
              >
                <span className="text-2xl flex-shrink-0">{topic.emoji}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-800 group-hover:text-green-700">{topic.label}</p>
                  <p className="text-xs text-gray-500">{topic.sublabel}</p>
                </div>
                <ChevronRight size={14} className="text-gray-400 group-hover:text-green-600 flex-shrink-0" />
              </button>
            ))}

            {/* Direct call */}
            {phoneNumber && (
              <a
                href={`tel:${phoneNumber.replace(/\s/g, '')}`}
                className="w-full flex items-center gap-3 p-2.5 rounded-xl bg-gray-50 hover:bg-gray-100 transition-all"
              >
                <span className="w-8 h-8 rounded-full bg-cyan-100 flex items-center justify-center flex-shrink-0">
                  <Phone size={14} className="text-cyan-700" />
                </span>
                <div>
                  <p className="text-xs font-semibold text-gray-700">Bizi Arayın</p>
                  <p className="text-xs text-gray-500">{phoneNumber}</p>
                </div>
              </a>
            )}
          </div>

          {/* Start button */}
          <div className="px-3 pb-3">
            <button
              onClick={() => openWhatsApp('Merhaba! AR Klima\'dan yardım almak istiyorum.')}
              className="w-full bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold py-2.5 rounded-xl transition-colors text-sm flex items-center justify-center gap-2"
            >
              <WhatsAppSVG className="w-4 h-4" />
              WhatsApp'ta Başlat
            </button>
          </div>
        </div>
      )}

      {/* Hint bubble */}
      {showHint && !isOpen && (
        <div
          className="bg-white rounded-xl shadow-lg px-3 py-2 text-xs text-gray-700 border border-gray-100 max-w-[160px] cursor-pointer"
          onClick={handleOpen}
          style={{ animation: 'slideUpFadeIn 0.3s ease-out' }}
        >
          💬 WhatsApp ile yazın!
          <button
            onClick={e => { e.stopPropagation(); setShowHint(false); }}
            className="ml-1 text-gray-400 hover:text-gray-600"
          >×</button>
        </div>
      )}

      {/* Main button */}
      <div className="relative">
        <button
          onClick={() => isOpen ? setIsOpen(false) : handleOpen()}
          className={`relative w-14 h-14 bg-[#25D366] hover:bg-[#20bd5a] rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 ${isOpen ? 'scale-95' : 'hover:scale-110'}`}
          title="WhatsApp ile yazın"
        >
          {isOpen ? (
            <X size={22} className="text-white" />
          ) : (
            <WhatsAppSVG className="w-7 h-7 text-white" />
          )}
        </button>
        {online && !isOpen && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-green-400 rounded-full border-2 border-white" />
        )}
        {!isOpen && (
          <div className="absolute inset-0 w-14 h-14 bg-[#25D366] rounded-full animate-ping opacity-20 pointer-events-none" />
        )}
      </div>

      <style>{`
        @keyframes slideUpFadeIn {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default WhatsAppButton;
